import { List, TextField, FunctionField, DatagridConfigurable, Pagination } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { REPORT_TYPES_WITH_MULTIPLE_FILES, HISTORICAL_REPORTS_STRINGS } from '../constants';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Tooltip } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { formatTimestamp, getDirectoryPath } from '../utils/reports/historicalReportsUtils';
import HistoricalReportsFilter from './HistoricalReportsFilter';

const HistoricalReports = () => {
  return (
    <List
      perPage={10}
      pagination={<Pagination />}
      resource="HistoricalReports"
      title="Report Status"
      filters={<HistoricalReportsFilter />}
      actions={false}
    >
      <Box sx={{ overflowX: 'auto', bgcolor: 'background.paper' }}>
        <DatagridConfigurable
          bulkActionButtons={false}
          sx={{
            tableLayout: 'auto',
            minWidth: '1800px',
            '& .MuiTableCell-head': {
              textAlign: 'center',
              whiteSpace: 'nowrap',
              px: 2,
            },
            '& .MuiTableCell-body': {
              textAlign: 'center',
              whiteSpace: 'nowrap',
              px: 2,
              wordBreak: 'normal',
            },
            '& .filePath-column': {
              width: '250px',
              minWidth: '250px',

              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .clientsCol': {
              width: '250px',
              minWidth: '250px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .tpasCol': {
              width: '200px',
              minWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        >
          <TextField
            source="id"
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.REQUEST_ID}
            sortable={false}
            sx={{ minWidth: 100 }}
          />
          <TextField
            source="report_name"
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.REPORT_NAME}
            sx={{ minWidth: 150 }}
          />
          <TextField
            source="requested_by"
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.REQUESTED_BY}
            sx={{ minWidth: 180 }}
          />
          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.REQUESTED_DATE}
            render={(record: any) => formatTimestamp(record.report_requested_date)}
            sx={{ minWidth: 200 }}
          />
          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.GENERATED_DATE}
            render={(record: any) => formatTimestamp(record.report_generated_date)}
            sx={{ minWidth: 200 }}
          />

          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.CLIENTS}
            cellClassName="clientsCol"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            render={(record: any) => {
              const clients = record.filters?.clients;
              const clientsText =
                clients && clients.length > 0 ? clients.join(', ') : HISTORICAL_REPORTS_STRINGS.PLACEHOLDER.EMPTY;
              return (
                <Typography
                  noWrap
                  title={clientsText}
                  sx={{
                    display: 'block',
                    width: '250px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {clientsText}
                </Typography>
              );
            }}
          />
          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.TPAS}
            cellClassName="tpasCol"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            render={(record: any) => {
              const tpas = record.filters?.tpas;
              const tpasText = tpas && tpas.length > 0 ? tpas.join(', ') : HISTORICAL_REPORTS_STRINGS.PLACEHOLDER.EMPTY;
              return (
                <Typography
                  noWrap
                  title={tpasText}
                  sx={{
                    width: '100%',
                    minWidth: 200,
                    textAlign: 'center',
                  }}
                >
                  {tpasText}
                </Typography>
              );
            }}
          />
          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.FILTERS.START_DATE}
            render={(record: any) => record.filters?.start_date || HISTORICAL_REPORTS_STRINGS.PLACEHOLDER.EMPTY}
            sx={{ minWidth: 120 }}
          />
          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.FILTERS.END_DATE}
            render={(record: any) => record.filters?.end_date || HISTORICAL_REPORTS_STRINGS.PLACEHOLDER.EMPTY}
            sx={{ minWidth: 120 }}
          />
          <TextField source="report_status" label={HISTORICAL_REPORTS_STRINGS.COLUMNS.STATUS} sx={{ minWidth: 120 }} />

          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.FILE_NAME}
            headerClassName="filePath-column"
            cellClassName="filePath-column"
            render={(record: any) => {
              const path = record.report_location || '';
              if (!path) return HISTORICAL_REPORTS_STRINGS.PLACEHOLDER.EMPTY;

              const clients = record.filters?.clients || [];
              const isMultiFileType = REPORT_TYPES_WITH_MULTIPLE_FILES.includes(record.report_name);
              const multipleClients =
                clients.length !== 1 || clients.includes(HISTORICAL_REPORTS_STRINGS.PLACEHOLDER.ALL_CLIENTS);

              const renderCell = (icon: React.ReactNode, text: string, tooltip: string) => (
                <Tooltip title={tooltip}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ width: '100%' }}>
                    {icon}
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        maxWidth: 200,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                </Tooltip>
              );

              if (isMultiFileType && multipleClients) {
                const directoryPath = getDirectoryPath(path);
                const displayName = record.report_name;

                return renderCell(
                  <FolderOutlinedIcon fontSize="small" color="action" />,
                  displayName,
                  `Directory: ${directoryPath}`
                );
              } else {
                const fileName = path.split(/[/\\]/).pop() || '';
                return renderCell(<ArticleOutlinedIcon fontSize="small" color="action" />, fileName, `File: ${path}`);
              }
            }}
            sx={{ width: 250 }}
          />

          <FunctionField
            label={HISTORICAL_REPORTS_STRINGS.COLUMNS.COPY_PATH}
            sx={{ maxWidth: 100 }}
            render={(record: any) => {
              const path = record.report_location;
              if (!path) return null;

              const isMultiFile = REPORT_TYPES_WITH_MULTIPLE_FILES.includes(record.report_name);
              const path_to_copy = isMultiFile ? getDirectoryPath(path) : path;
              const tooltipTitle = isMultiFile
                ? HISTORICAL_REPORTS_STRINGS.COPY_PATH.DIRECTORY
                : HISTORICAL_REPORTS_STRINGS.COPY_PATH.FILE;

              return (
                <Tooltip title={tooltipTitle}>
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(path_to_copy);
                      toast.success(HISTORICAL_REPORTS_STRINGS.COPY_PATH.SUCCESS);
                    }}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              );
            }}
          />
        </DatagridConfigurable>
      </Box>
    </List>
  );
};

export default HistoricalReports;
